import React from 'react';
import { Card, Title, AreaChart, Text, Subtitle, Grid, Col } from '@tremor/react';

export default function RespGrowthChart() {
  // Data for RESP growth with and without CESG
  // Assuming $2,500 annual contribution with 5% return
  const respData = [
    { year: 1, contributionsOnly: 2625, withCESG: 3150 },
    { year: 2, contributionsOnly: 5381, withCESG: 6458 },
    { year: 3, contributionsOnly: 8275, withCESG: 9930 },
    { year: 4, contributionsOnly: 11314, withCESG: 13577 },
    { year: 5, contributionsOnly: 14505, withCESG: 17406 },
    { year: 6, contributionsOnly: 17855, withCESG: 21426 },
    { year: 7, contributionsOnly: 21373, withCESG: 25648 },
    { year: 8, contributionsOnly: 25067, withCESG: 30080 },
    { year: 9, contributionsOnly: 28945, withCESG: 34734 },
    { year: 10, contributionsOnly: 33018, withCESG: 39621 },
    { year: 11, contributionsOnly: 37294, withCESG: 44752 },
    { year: 12, contributionsOnly: 41783, withCESG: 50140 },
    { year: 13, contributionsOnly: 46498, withCESG: 55797 },
    { year: 14, contributionsOnly: 51448, withCESG: 61737 },
    { year: 15, contributionsOnly: 56645, withCESG: 67974 },
    { year: 16, contributionsOnly: 62103, withCESG: 74523 },
    { year: 17, contributionsOnly: 67833, withCESG: 81399 },
    { year: 18, contributionsOnly: 73850, withCESG: 88619 },
  ];

  // Calculate the CESG impact
  const finalWithoutCESG = respData[respData.length - 1].contributionsOnly;
  const finalWithCESG = respData[respData.length - 1].withCESG;
  const cesgImpact = finalWithCESG - finalWithoutCESG;
  const cesgPercentage = ((cesgImpact / finalWithoutCESG) * 100).toFixed(1);

  return (
    <Card className="mt-8 mb-8 bg-blue-50 border border-blue-100">
      <Title className="text-blue-800 mb-2">RESP Growth: Impact of Government Grants</Title>
      <Subtitle className="text-blue-600 mb-4">
        $2,500 Annual Contribution with 5% Return
      </Subtitle>
      
      <AreaChart
        className="h-72 mt-4"
        data={respData}
        index="year"
        categories={["contributionsOnly", "withCESG"]}
        colors={["indigo", "blue"]}
        valueFormatter={(number) => `$${number.toLocaleString()}`}
        showLegend={true}
        showGridLines={true}
        showAnimation={true}
        curveType="monotone"
        customTooltip={(props) => {
          const { payload, active } = props;
          if (!active || !payload) return null;
          
          const year = payload[0]?.payload.year;
          const contributionsOnly = payload[0]?.value || 0;
          const withCESG = payload[1]?.value || 0;
          const difference = Number(withCESG) - Number(contributionsOnly);
          
          return (
            <div className="p-2 border border-tremor-border shadow-tremor-dropdown rounded-tremor-default bg-tremor-background">
              <div className="font-semibold text-tremor-content-emphasis">Year {year}</div>
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-indigo-500"></span>
                  <span className="text-tremor-content">Without CESG: ${contributionsOnly?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-blue-500"></span>
                  <span className="text-tremor-content">With CESG: ${withCESG?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 pt-1 border-t border-tremor-border">
                  <span className="text-tremor-content-emphasis">CESG Impact: ${difference?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          );
        }}
      />
      
      <Grid numItems={1} numItemsSm={2} numItemsLg={2} className="gap-4 mt-6">
        <Card className="bg-white shadow-sm">
          <Text className="text-center text-blue-700">Total CESG Contribution</Text>
          <Title className="text-center text-blue-800 mt-2">${cesgImpact.toLocaleString()}</Title>
        </Card>
        <Card className="bg-white shadow-sm">
          <Text className="text-center text-blue-700">Growth Enhancement</Text>
          <Title className="text-center text-blue-800 mt-2">{cesgPercentage}%</Title>
        </Card>
      </Grid>
      
      <Text className="text-xs text-gray-500 mt-4">
        Note: This chart illustrates the impact of the Canada Education Savings Grant (CESG) on RESP growth over 18 years.
        The blue area shows growth with both contributions and CESG, while the indigo area shows growth with contributions only.
        Assumes a consistent 5% annual return and maximum CESG eligibility.
      </Text>
    </Card>
  );
}
